import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { RefreshCw, Loader2 } from "lucide-react";
import { BACKEND_URL } from "../../lib/apiBase";

const API = `${BACKEND_URL}/api`;

const ALL_PARTNER_MAILS = [
  { id: "P_QUICKSTART", group: "Nurture", offset: 1 },
  { id: "P2", group: "Nurture", offset: 4 },
  { id: "P3", group: "Nurture", offset: 7 },
  { id: "P4", group: "Nurture", offset: 14 },
  { id: "P5", group: "Nurture", offset: 30 },
  { id: "PARTNER_SETUP_REMINDER_7", group: "Setup", offset: 7, short: "Setup-1" },
  { id: "PARTNER_SETUP_REMINDER_14", group: "Setup", offset: 14, short: "Setup-2" },
  { id: "PARTNER_SETUP_REMINDER_21", group: "Setup", offset: 30, short: "Setup-3" },
];


function fmtDateShort(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit" }); }
  catch { return "—"; }
}
function fmtDateTimeShort(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" }); }
  catch { return "—"; }
}

export default function UpcomingPartnerMails({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("active");

  const load = async () => {
    if (!token) return;
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`${API}/admin/partner-reminders/upcoming`, { headers: { "x-admin-token": token } });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Konnte Übersicht nicht laden.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  // Fetch the partners-mail-overview for per-partner mail history
  const [mailOverview, setMailOverview] = useState(null);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/partners-mail-overview`, { headers: { "x-admin-token": token } })
      .then((r) => setMailOverview(r.data))
      .catch(() => {});
  }, [token]);
  const sentByPartner = useMemo(() => {
    if (!mailOverview) return {};
    const out = {};
    for (const p of mailOverview.partners || []) {
      const m = {};
      for (const h of (p.mails || [])) {
        if (!m[h.mail_id] || (h.sent_at > (m[h.mail_id]?.sent_at || ""))) {
          m[h.mail_id] = { status: h.status, sent_at: h.sent_at };
        }
      }
      out[p.id] = m;
    }
    return out;
  }, [mailOverview]);

  const rows = (data?.rows || []).filter((r) => {
    if (filter === "all") return true;
    if (filter === "active") return !r.drip_opt_out && (r.setup_reason !== "setup_complete" || r.nurture_pending.length > 0 || (sentByPartner[r.id] && Object.keys(sentByPartner[r.id]).length > 0));
    if (filter === "complete") return r.setup_reason === "setup_complete";
    if (filter === "opt_out") return r.drip_opt_out;
    return true;
  });

  const stats = useMemo(() => {
    const all = data?.rows || [];
    return {
      total: all.length,
      active: all.filter((r) => !r.drip_opt_out && (r.setup_reason !== "setup_complete" || r.nurture_pending.length > 0 || (sentByPartner[r.id] && Object.keys(sentByPartner[r.id]).length > 0))).length,
      complete: all.filter((r) => r.setup_reason === "setup_complete").length,
      optOut: all.filter((r) => r.drip_opt_out).length,
    };
  }, [data, sentByPartner]);

  const globalDripEnabled = !!data?.global_drip_enabled;

  // Manual send — same flow as before, triggered by clicking a cell.
  const [sendingKey, setSendingKey] = useState("");
  const sendOne = async (partner, mailId) => {
    const reset = partner.drip_opt_out
      ? window.confirm(`${partner.name} ist aktuell auf drip_opt_out=true. Opt-out zurücksetzen, damit der Cron danach weiterläuft?`)
      : false;
    if (!window.confirm(`${mailId} jetzt manuell an ${partner.name} (${partner.email}) senden?`)) return;
    const key = `${partner.id}:${mailId}`;
    setSendingKey(key);
    try {
      const r = await axios.post(
        `${API}/admin/partner-reminders/send-one`,
        { partner_id: partner.id, mail_id: mailId, reset_optout: reset },
        { headers: { "x-admin-token": token } },
      );
      window.alert(`✅ ${r.data.mail_id} an ${r.data.sent_to} versendet${r.data.opt_out_reset ? "\n(Opt-out wurde zurückgesetzt)" : ""}`);
      load();
      // also refresh mail-overview so the matrix updates immediately
      axios.get(`${API}/admin/partners-mail-overview`, { headers: { "x-admin-token": token } })
        .then((r2) => setMailOverview(r2.data)).catch(() => {});
    } catch (e) {
      window.alert(`❌ ${e?.response?.data?.detail || "Send fehlgeschlagen."}`);
    } finally { setSendingKey(""); }
  };

  return (
    <section className="al-card p-6 mt-8" data-testid="partner-upcoming-mails">
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <ClockIcon />
          <h2 className="font-display font-bold text-lg">Partner-Mail-Status</h2>
          <span className={`font-mono-tab text-[11px] font-bold rounded-full px-2 py-0.5 border ${globalDripEnabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
            Auto-Drip: {globalDripEnabled ? "AKTIV" : "PAUSIERT"}
          </span>
        </div>
        <button
          type="button"
          onClick={() => { load(); axios.get(`${API}/admin/partners-mail-overview`, { headers: { "x-admin-token": token } }).then((r) => setMailOverview(r.data)).catch(() => {}); }}
          disabled={loading}
          data-testid="upcoming-mails-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
        </button>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Status-Matrix: pro Partner zeigt jede Spalte eine Mail. <span className="inline-block w-3 h-3 rounded bg-emerald-500 align-middle mx-1"></span> versendet (Datum im Tooltip), <span className="inline-block w-3 h-3 rounded bg-violet-300 align-middle mx-1"></span> geplant, <span className="inline-block w-3 h-3 rounded bg-slate-200 align-middle mx-1"></span> entfällt. <strong>Klick auf eine Zelle</strong> → diese Mail wird manuell an diesen Partner gesendet (Dedup-Schutz greift automatisch).
      </p>

      {err && <div className="mb-3 p-3 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm">{err}</div>}

      <div className="flex items-center gap-2 mb-4 flex-wrap text-xs">
        {[
          { key: "active", label: `Aktive Drips (${stats.active})`, color: "bg-violet-100 text-violet-800" },
          { key: "complete", label: `Setup ✓ (${stats.complete})`, color: "bg-emerald-100 text-emerald-800" },
          { key: "opt_out", label: `Opt-out (${stats.optOut})`, color: "bg-rose-100 text-rose-700" },
          { key: "all", label: `Alle (${stats.total})`, color: "bg-slate-100 text-slate-700" },
        ].map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setFilter(c.key)}
            data-testid={`upcoming-filter-${c.key}`}
            className={`px-3 py-1 rounded-full font-bold transition-all ${filter === c.key ? c.color + " ring-2 ring-offset-1 ring-violet-300" : "bg-slate-50 text-slate-500 hover:bg-slate-100"}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {loading && !data ? (
        <p className="text-sm text-slate-500">Lade …</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500 py-4" data-testid="upcoming-mails-empty">Keine Partner in dieser Kategorie.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-separate border-spacing-0">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest font-bold text-slate-500 bg-slate-50">
                <th className="text-left py-2 px-3 sticky left-0 bg-slate-50 z-10 border-b border-slate-200">Partner</th>
                {ALL_PARTNER_MAILS.map((m) => (
                  <th key={m.id} className="py-2 px-1.5 text-center border-b border-slate-200" title={`${m.id} · Tag ${m.offset}`}>
                    <div className="font-mono-tab text-[10px]">{m.short || m.id.replace("P_", "P-")}</div>
                    <div className="text-[9px] text-slate-400 font-normal mt-0.5">T{m.offset}</div>
                  </th>
                ))}
                <th className="py-2 px-3 text-right border-b border-slate-200">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const sentMap = sentByPartner[r.id] || {};
                const isSetupComplete = r.setup_reason === "setup_complete";
                const pendingMap = {};
                for (const n of r.nurture_pending) pendingMap[n.mail_id] = n;
                if (r.setup_next) pendingMap[r.setup_next.mail_id] = r.setup_next;
                return (
                  <tr key={r.id} data-testid={`upcoming-row-${r.id}`} className="hover:bg-violet-50/30 transition-colors">
                    <td className="py-2 px-3 sticky left-0 bg-white z-10 border-b border-slate-100 min-w-[200px]">
                      <div className="font-display font-semibold text-slate-900 truncate">{r.name}</div>
                      <div className="text-[11px] text-slate-500 font-mono-tab truncate">{r.email}</div>
                    </td>
                    {ALL_PARTNER_MAILS.map((mailDef) => {
                      const sent = sentMap[mailDef.id];
                      const pending = pendingMap[mailDef.id];
                      const isSetupMail = mailDef.group === "Setup";
                      // Determine cell state
                      let bg, dot, tooltip;
                      if (sent && sent.status === "sent") {
                        bg = "bg-emerald-100"; dot = "●";
                        tooltip = `${mailDef.id}: versendet am ${fmtDateTimeShort(sent.sent_at)}`;
                      } else if (sent && sent.status === "failed") {
                        bg = "bg-rose-100"; dot = "✕";
                        tooltip = `${mailDef.id}: FEHLGESCHLAGEN am ${fmtDateTimeShort(sent.sent_at)}`;
                      } else if (isSetupMail && isSetupComplete) {
                        bg = "bg-slate-100"; dot = "—";
                        tooltip = `${mailDef.id}: entfällt (Setup ✓)`;
                      } else if (r.drip_opt_out) {
                        bg = "bg-slate-100"; dot = "—";
                        tooltip = `${mailDef.id}: pausiert (drip_opt_out)`;
                      } else if (pending) {
                        bg = pending.is_overdue ? "bg-amber-200" : "bg-violet-200";
                        dot = pending.is_overdue ? "!" : "○";
                        tooltip = `${mailDef.id}: geplant für ${fmtDateTimeShort(pending.eta_at)}${pending.is_overdue ? " (überfällig)" : ""}`;
                      } else {
                        bg = "bg-slate-50"; dot = "·";
                        tooltip = `${mailDef.id}: noch nicht im Plan`;
                      }
                      return (
                        <td key={mailDef.id} className="py-1.5 px-1 text-center border-b border-slate-100">
                          <button
                            type="button"
                            onClick={() => sendOne(r, mailDef.id)}
                            disabled={sendingKey === `${r.id}:${mailDef.id}`}
                            data-testid={`upcoming-cell-${r.id}-${mailDef.id}`}
                            className={`mx-auto w-9 h-7 rounded ${bg} text-[10px] font-bold text-slate-700 flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-violet-400 hover:ring-offset-1 transition-all disabled:opacity-50`}
                            title={`${tooltip} · Klick zum manuellen Senden`}
                          >
                            {sendingKey === `${r.id}:${mailDef.id}` ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : sent && sent.status === "sent" ? (
                              fmtDateShort(sent.sent_at)
                            ) : (
                              dot
                            )}
                          </button>
                        </td>
                      );
                    })}
                    <td className="py-2 px-3 text-right border-b border-slate-100 min-w-[120px]">
                      {r.drip_opt_out ? (
                        <span className="text-[10px] uppercase font-bold rounded-full px-2 py-0.5 border bg-rose-50 text-rose-700 border-rose-200">Opt-out</span>
                      ) : isSetupComplete ? (
                        <span className="text-[10px] uppercase font-bold rounded-full px-2 py-0.5 border bg-emerald-50 text-emerald-700 border-emerald-200">Setup ✓</span>
                      ) : (
                        <span className="text-[10px] uppercase font-bold rounded-full px-2 py-0.5 border bg-amber-50 text-amber-800 border-amber-200" title={(r.missing_setup_fields || []).join(", ")}>
                          Setup fehlt
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const ClockIcon = () => (
  <svg className="h-4 w-4 text-[var(--al-purple)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
